import { dateLib, moneyLib, propertyLib, tenantLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { fileType, tenantApplicationType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { type ChangeEvent, type DragEvent, useCallback, useEffect, useRef, useState } from "react"
import { Camera, FileText, Plus, X } from "react-feather"
import { useFieldArray, useForm } from "react-hook-form"
import { z } from "zod"
import { UPLOAD_ACCEPT, uploadFile } from "@/api/client/file"
import { tenantApplicationApi } from "@/api/client/tenant-application"
import type {
  PropertyPublicList200PropertiesItem,
  PropertyPublicList200PropertiesItemRoomsItem,
  TenantApplicationCreateBodyPurpose,
} from "@/api/generated/models"
import logo from "@/assets/logo.png"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Combobox } from "@/components/ui/combobox"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { PriceWithTax } from "@/components/ui/price-with-tax"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  FormFieldLabel,
  handleNumericInput,
  handleNumericPaste,
  OccupantsFields,
  OtherFeesFields,
  ParkingFields,
  toOccupantsPayload,
} from "./components"
import { usePropertySearch } from "./hooks"

// 添付ファイルの型定義
interface ImageFile {
  file: File
  preview: string
}

const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (fileType: string | null | undefined, fileName?: string | null) =>
  fileType?.startsWith("image/") || (!!fileName && IMAGE_FILE_EXTENSION_REGEX.test(fileName))

// 添付ファイルプレビューコンポーネント
function ImagePreview({
  attachment,
  onDelete,
  className,
}: {
  attachment: ImageFile
  onDelete: () => void
  className?: string
}) {
  const isImage = isImageFile(attachment.file.type, attachment.file.name)

  return (
    <div className={cn("relative inline-block", className)}>
      {isImage ? (
        <img
          src={attachment.preview}
          alt="プレビュー"
          className="w-full h-32 object-cover rounded-md border border-slate-200"
        />
      ) : (
        <div className="flex h-32 w-full min-w-[220px] items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-4 text-slate-600">
          <FileText className="size-8 shrink-0 text-slate-400" />
          <span className="min-w-0 truncate text-sm font-semibold">{attachment.file.name}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onDelete}
        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
        aria-label="ファイルを削除"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

// ファイル選択ボタンコンポーネント
function ImageSelectButton({
  onSelect,
  hasFile,
}: {
  onSelect: (file: File) => void
  hasFile: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onSelect(file)
    }
    // inputをリセットして同じファイルを再度選択可能にする
    if (inputRef.current) {
      inputRef.current.value = ""
    }
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) {
      onSelect(file)
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.dataTransfer.types.includes("Files")) {
      setIsDragging(true)
    }
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return
    }
    setIsDragging(false)
  }

  return (
    <div
      className={cn(
        "rounded-md transition-colors",
        isDragging && "bg-blue-50 ring-2 ring-inset ring-blue-300"
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        onChange={handleChange}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        onClick={handleClick}
        className="h-10 w-full border-[#9eafc7] text-[#354267] font-bold rounded-[6px]"
      >
        <Camera className="size-[14px] mr-2" />
        {hasFile ? "ファイルを変更" : "ファイルを選択 / ドロップ"}
      </Button>
    </div>
  )
}

interface Step {
  number: number
  label: string
}

const STEPS: Step[] = [
  { number: 1, label: "サービス\n利用規約" },
  { number: 2, label: "火災保険\n意向確認" },
  { number: 3, label: "申込フォーム入力" },
  { number: 4, label: "確認画面" },
  { number: 5, label: "完了" },
]

function StepProgress({ currentStep }: { currentStep: number }) {
  return (
    <div className="relative w-full px-10">
      {/* Progress Line */}
      <div className="absolute top-[17px] left-[64px] right-[64px] h-[2px] bg-slate-200" />

      {/* Steps */}
      <div className="relative flex items-start justify-between">
        {STEPS.map((step) => (
          <div key={step.number} className="flex flex-col items-center gap-2 w-12">
            {/* Circle */}
            <div
              className={cn(
                "relative z-10 flex size-[34px] items-center justify-center rounded-full text-sm font-bold",
                step.number === currentStep
                  ? "bg-[#354267] text-white"
                  : "bg-slate-300 border-2 border-slate-200 text-slate-500"
              )}
            >
              {step.number}
            </div>
            {/* Label */}
            <span
              className={cn(
                "text-xs font-bold text-center text-slate-600 whitespace-pre-line leading-[14px]"
              )}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Step 2: 火災保険のご案内（意向確認）
const FIRE_INSURANCE_CONTENT = `オーナーズエージェント株式会社から火災保険のご提案です。
オーナーズエージェント株式会社では、エージェントコールへお申込みされる方へ家財の補償と大家さんへの賠償責任や日常生活での賠償責任を補償する楽天損保の火災保険「リビングアシスト総合保険（総括契約）」への加入をご提案しております。
「リビングアシスト総合保険（総括契約）」へのご加入を希望される場合、以下にご契約内容の詳細をご確認いただき、お客様のご意向と契約内容が合致していることをご確認のうえ、本書下段のチェックボックスにチェックをお願いします。

補償期間：2026年10月1日～2027年10月1日
保険料：月額650円（家財：170万円　借家人賠償：2,000万円　個人賠償：1億円　修理費用：100万円）

【書類の電子交付について】
お申込手続きに関する以下の書類は、添付資料をご確認いただくことになります。
あらかじめご了承ください。

・重要事項説明書
・その他ご契約に関する重要書類

[（PDF）重要事項説明書](https://drive.google.com/file/d/13Ih-7KdAg_23-q62WlUeFw7gKuKNuZFh/view?usp=drive_link)

[（PDF）商品パンフレット](https://drive.google.com/file/d/1efCUPIoMb5JcNsbvHxOyMbnIF_XdP5df/view?usp=drive_link)

[（PDF）補償制度のご案内チラシ](https://drive.google.com/file/d/1RRbgAGRjWU9d_7WZtTnV4YCdBX9GLYjS/view?usp=drive_link)

[（PDF）緊急サポートサービス](https://drive.google.com/file/d/1AHq8L6j78I-TmTN0TOzVrVVDyECV0kDL/view?usp=drive_link)

※ご意向とリビングアシスト総合保険（総括契約）の契約内容に相違がございましたら、オーナーズエージェント株式会社までご連絡ください。`

// Step 2: サービス利用規約
const SERVICE_TERMS_CONTENT = `入居者総合サービス【○○サポート】利用規約

制定：2025年○月1日
第１章　総則
第１条（名称）
株式会社○○（以下「○○」という）の入居者総合サービスを【○○サポート】（以下「本サービス」という）と名称します。

第2条（目的）
本サービスは、○○が管理する賃貸物件のご入居者様に対し、安心で快適な賃貸生活に役立つサービスの提供を目的としています。

第３条（サービス内容）
本サービスは、○○が管理する賃貸物件のご入居者様専用のサービスです。
本サービスは、「火災補償制度」「〇〇」「〇〇」により構成され、それらの提供会社（以下「本サービス提供会社」といいます。）は以下のとおりです。
1. 火災補償制度（本サービス提供会社：楽天損害保険株式会社）
2. 緊急サポートサービス（本サービス提供会社：楽天損害保険株式会社）
3. 〇〇（本サービス提供会社：〇〇）

第４条（本規約の適用）
1. 本サービスは○○が管理する賃貸物件をご契約され、本利用規約を承諾し本サービスへ加入するご入居者様（以下「加入者」という）に適用するものとします。
2. 加入者は、本サービスの加入費として、賃貸住宅の賃料と併せて当社の定める方法にて、月額○○円（税込）を支払うものとします。
3. 本サービスの加入期間は、対象となる賃貸住宅の賃貸借契約間と同一とし、対象となる賃貸住宅の賃貸借契約が解約、もしくはその他の理由によって終了した場合は、本サービスも同時に終了するものとします。また当社が対象となる賃貸住宅の管理会社としての地位を失った場合、本サービスは自動的に終了します。この場合、家主様もしくは新管理会社より賃料支払方法等の変更通知が届きます。その変更と同時に前項記載の利用料の支払いも終了し本サービスの提供も終了いたします。
4. 加入者が賃貸借契約を更新した場合は、本サービスの加入期間についても同時に更新されます。
5. ○○に支払われた利用料は、途中退会や利用資格取消その他の理由の如何を問わず、または加入者が実際に本サービスを利用したか否かに関係なく、原則として返金しないものとします。ただし、当社に債務不履行や不法行為がある場合はこの限りではありません。
6. 加入者は、本サービスの利用において本規約以外に本サービス提携会社が定めるすべての規約、規則、注意事項を承諾して利用するものとします。

第5条（加入者）
1. 入居者総合サービス【○○サポート】の契約者名欄に記載されている者を加入者とします。
2. 本サービスは、加入者または加入者の同居家族のみが利用できるものとし、それ以外のものが利用することはできません。また、サービス利用時の加入者の住所が届出済の住所と異なる場合には、利用することができません。

第6条（サービス内容の変更及びサービスの終了等）
1. 本サービスは、本サービス提供会社の規約の変更または当社と当社以外の本サービス提供会社との間の本サービスに関する契約もしくは当社と賃貸住宅の賃借人との管理委託契約が変更・解除・解約等した場合には、加入者の承認または加入者への事前通知を要することなく、その内容を直ちに変更・中止・終了することができるものとします。
2. 当社および本サービス提供会社は、本サービスの変更または終了等に起因して発生した損害について、故意または重大な過失がない場合に限り損害賠償その他の責任を負わないものとします。ただし、加入者が消費者契約法上の消費者に該当する場合はこの限りではありません。

第7条（加入者情報の変更）
1. 本サービスの加入者は、当社に届け出た入居者、同居家族、連絡先および住所等の情報（以下「登録情報」といいます。）に変更があった場合、当社に申し出を行い、所定の方法により遅滞なく変更手続きを行うものとします。
2. 加入者および入居者、同居家族の名義変更は、第4条各項の要件を充たし、所定の加入費を支払っている場合に行うことができます。
3. 登録情報の不備、変更手続きの不履行や遅滞等により加入者が被ったいかなる不利益に対して、当社および本サービス提供会社は、責任を負わないものとします。

第8条（禁止行為）
本サービスの利用にあたって以下の行為をしてはならないものとします。
1. 本サービスを利用資格のない第三者に知らしめ、本サービスを提供させる行為
2. 本サービスで定めた内容を超えるサービス提供を求める行為又は本規約を逸脱する行為及びそれに類する行為
3. 本サービスに係る個人・法人・団体に不利益又は損害を与える行為又は与える恐れのある行為
4. 本サービスに係る個人・法人・団体を誹謗中傷する行為
5. 法律に違反する行為又は違反の恐れのある行為
6. 犯罪、反社会的行為を含む公序良俗に反する行為又はそれに関連する行為
7. その他、本サービス利用の一般的なマナーやモラルを著しく逸脱し不適切と判断される行為

第9条（免責）
1. ○○は、以下の場合に本サービスが一時的に停止又は中止若しくは変更されたとしても、加入者及びご入居されるご家族様が直接的又は間接的に被った一切の損害、損失、不利益等について、いかなる責任も負わないものとします。ただし、火災補償制度の補償範囲内は除くものとします。
  a. 火災、地震、噴火、洪水、落雷、大雪等の天変地異が生じた場合
  b. 戦争、内乱、テロ、暴動、騒乱等の社会不安が生じた場合
  c. 本サービスに係るシステムの定期点検又は緊急に行う保守・点検
  d. その他、本サービスの中断の必要があると○○が判断した場合
2. 当社は本サービスの利用により発生した加入者の損害または、第三者に生じた損害（他社との間で生じたトラブルに起因する損害を含みます。）および本サービスを利用できなかったことにより加入者または第三者に生じた損害について、故意または重大な過失がない限り損害賠償その他の責任を負わないものとします。ただし、加入者が消費者契約法上の消費者に該当する場合はこの限りではありません。
3. 本サービスの利用に関し当社および本サービス提供会社が損害賠償責任を負う場合、加入者が現実に被った直接かつ通常の損害に限り責任を負うものとし、予見の有無に関わらず、特別の事情から生じた損害、逸失利益、間接利益、付随的・派生的損害については責任を負いません。また損害額の限度は、加入者が当社に本サービスの対価として支払った総額の範囲内とします。ただし、加入者が消費者契約法上の消費者に該当する場合はこの限りではありません。

第１0条（個人情報の提供及び利用）
1. 当社および本サービス提供会社は、登録情報および本サービスの加入者の個人情報について、「個人情報の保護に関する法律」に従い、善良な管理者の注意義務をもって管理し、必要な保護装置を講じた上で取り扱います。
①加入者が本サービスの提供を受けるために自ら告知する以下の個人情報。
　氏名、性別、生年月日、年齢、職業、勤務先、同居家族名（加入者本人以外の場合）、
　郵便番号、住所、電話番号、FAX番号、メールアドレス　等
②加入者が本サービスの利用にあたって申し出た事項（利用内容、申込内容等）
2. 当社は、加入者の個人情報を厳に秘密として管理し、加入者の事前の承諾なく第三者に提供又は開示しないものとします。ただし次の各場合には、加入者の事前の同意なくして当社は、第三者に対して加入者の個人情報を提供又は開示できるものとします。
  1. 法令に基づく場合
  2. 人の生命、身体又は財産の他のために必要がある場合であって、加入者の同意を得ることが困難である場合
  3. 公衆の衛生の向上又は児童の健全な育成の推進のために特に必要がある場合であって、加入者の同意を得ることが困難である場合
  4. 国の機関若しくは地方公共団体又はその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、加入者の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがある場合。

第１1条（管轄裁判所）
本契約に関し訴訟の必要が生じたときは、東京地方裁判所を第一審の専属的合意管轄裁判所とします。

第２章　火災補償制度
第１2条（火災補償制度）
1. このサービスは、万一の事故（火災による家財損失等）や賠償責任（不注意による失火、階下への漏水事故等）の際に損害保険から損害に対する補償が提供されるものです。
2. 前項の損害保険の概要は下記のとおりです。
○○が保険契約者となり、楽天損害保険株式会社との間で損害保険契約を締結します。お客様は当該保険契約の被保険者（保険金受取人）となり、本サービス料の中から保険料相当額を負担していただきます。なお、賃貸借契約の終了あるいは物件解約の場合には、前項の損害保険の補償は終了します。
(記載事項)
保険種類：リビングアシスト総合保険
補償内容：別紙賃貸入居サポートパンフレットに記載のとおり
ただしリビングアシスト総合保険の改定により保険料相当額または補償内容に変更が生じる場合があります。改定が生じた際のお知らせ、改定の内容に関しては楽天損害保険株式会社のホームページに掲載します。
保険期間：賃貸借契約期間に同じ
引受保険会社：楽天損害保険株式会社
取扱代理店：○○
※賃貸借契約締結時に同一の所在地（入居戸室）を対象とした他の保険契約を締結している場合は本サービスの提供はいたしませんのでお申し出ください。お申し出のない場合は当該保険契約の被保険者になることに同意いただいたものとみなします。

第３章　緊急サポートサービス
第１3条（緊急サポートサービス）
1. 本サービスの加入者は、【○○サポート】にご同意のうえ利用を申込むことにより、本サービス提供会社が提供する緊急サポートサービスをご利用いただけます。
2. 緊急サポートサービスは、本サービスに加入し当社が管理する賃貸住宅の入居者に限り利用できるものとします。
3. 緊急サポートサービスの利用者は、当該サービスの保証期間中に合計3回まで、本サービス提供会社所定の方法で次の各サービスを利用することができます。なお、本サービス提供会社所定の応急処置（目安：30分程度の軽作業）に要する作業料および出張料は請求されないものとします。但し、本サービス提供会社所定の応急処置を超える作業料および部品代は、別途、利用者にご負担いただきます。
  a. 水のトラブル：トイレの詰まりや配管からの水漏れなどの応急処置
  b. 鍵のトラブル：鍵を紛失してしまった場合の鍵開け
  c. ガラスのトラブル：ガラスが割れてしまった場合の片づけや養生作業
  d. エアコンのトラブル：エアコンホースの詰まりによる水漏れなどの応急処置
  e. 給湯器のトラブル：お湯が出ない、追い焚きができないなどの給湯器の応急処置
※具体的な応急処置・作業の内容等は、本サービス提供会社にお問い合わせください。
4. 緊急サポートサービスは、離島など一部地域ではご利用いただけない場合があります。また、緊急サポートサービスの内容は、本サービス提供会社により予告なく変更・中止される場合がございますので、予めご了承ください。

第４章　〇〇サービス
第１4条（〇〇サービス）

以上`

const DEFAULT_TENANT_APPLICATION_PURPOSE = tenantApplicationType.TENANT_APPLICATION_PURPOSE_LIVING

// Step 3: 申込フォーム バリデーションスキーマ
const applicationFormSchema = z
  .object({
    // ①ご契約者情報
    contractType: z.enum(tenantApplicationType.CONTRACT_TYPE_VALUES, {
      error: "契約区分を選択してください",
    }),
    name: z.string().min(1, "ご契約者氏名を入力してください"),
    nameKana: z
      .string()
      .min(1, "ご契約者氏名（カナ）を入力してください")
      .regex(/^[ァ-ヶー 　]+$/, "全角カタカナで入力してください"),
    birthday: z.date().nullable().optional(),
    phone1: tenantApplicationType.phone1Z,
    phone2: tenantApplicationType.phone2Z,
    phone3: tenantApplicationType.phone3Z,
    phone4: tenantApplicationType.phone4Z.default(""),
    phone5: tenantApplicationType.phone5Z.default(""),
    phone6: tenantApplicationType.phone6Z.default(""),
    email: z
      .string()
      .min(1, "メールアドレスを入力してください")
      .email("有効なメールアドレスを入力してください"),
    leaseStartDate: z.date({ error: "賃貸借契約開始日を選択してください" }),
    // ②物件情報
    roomId: z
      .number({ error: "物件と部屋を選択してください" })
      .min(1, "物件と部屋を選択してください"),
    usage: z.string().default(DEFAULT_TENANT_APPLICATION_PURPOSE),
    // ③部屋情報
    rent: z.string().min(1, "家賃を入力してください"),
    commonFee: z.string().min(1, "共益費・管理費を入力してください"),
    hasParking: z.boolean().default(false),
    parkingLotNumber: z.string().optional(),
    parkingFee: z.string().optional(),
    otherFees: z
      .array(
        z.object({
          name: z.string(),
          baseFee: z.string(),
          tax: z.string(),
        })
      )
      .default([{ name: "", baseFee: "", tax: "" }]),
    // ④仲介会社情報
    agentCompanyName: z.string().min(1, "仲介会社名を入力してください"),
    agentPhone: z
      .string()
      .min(1, "電話番号を入力してください")
      .regex(/^[0-9]+$/, "半角数字で入力してください"),
    agentOtherContact: z.string().optional(),
    // ⑤ファイル添付
    leaseContractImage: z.any().optional(),
    importantDocImage: z.any().optional(),
    otherImages: z
      .array(
        z.object({
          title: z.string(),
          file: z.any(),
        })
      )
      .optional(),
    // ⑥入居者情報
    occupants: z
      .array(
        z.object({
          name: z.string().min(1, "入居者の氏名を入力してください"),
          nameKana: z.string(),
          phone: z.string(),
          birthday: z.string(),
          note: z.string(),
        })
      )
      .default([]),
  })
  .refine(
    (data) =>
      data.contractType !== tenantApplicationType.CONTRACT_TYPE_INDIVIDUAL || !!data.birthday,
    {
      message: "生年月日を選択してください",
      path: ["birthday"],
    }
  )
  .refine(
    (data) => {
      const values = [data.phone4, data.phone5, data.phone6]
      return values.every((value) => !value) || values.every((value) => !!value)
    },
    {
      message: "電話番号2を入力する場合はすべて入力してください",
      path: ["phone4"],
    }
  )

type ApplicationFormValues = z.infer<typeof applicationFormSchema>

// 確認画面に渡すデータの型
interface ConfirmationData {
  formValues: ApplicationFormValues
  leaseContractImage: ImageFile | null
  importantDocImage: ImageFile | null
  otherImages: (ImageFile | null)[]
  selectedProperty: PropertyPublicList200PropertiesItem | null
  selectedRoom: PropertyPublicList200PropertiesItemRoomsItem | null
}

// フォームセクションタイトルコンポーネント
function FormSectionTitle({ number, title }: { number: number; title: string }) {
  const sectionNumbers = ["", "①", "②", "③", "④", "⑤", "⑥"]

  return (
    <div className="bg-[#ebf0f6] px-[14px] py-[12px] w-full">
      <div className="flex items-center">
        <span className="text-[#354267] text-sm font-bold tracking-[-0.168px]">
          {`${sectionNumbers[number] ?? `${number}.`} ${title}`}
        </span>
      </div>
    </div>
  )
}

// 読み取り専用フィールドコンポーネント
function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-start w-full">
      <FormFieldLabel label={label} disabled />
      <div className="py-[5px]">
        <span className="text-sm font-medium text-[#3f4652] tracking-[-0.168px] leading-5 whitespace-pre-line">
          {value}
        </span>
      </div>
    </div>
  )
}

function renderContentWithLinks(text: string) {
  const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/g
  const elements = []
  let lastIndex = 0

  for (const match of text.matchAll(linkRegex)) {
    const matchText = match[0]
    const matchIndex = match.index ?? 0

    if (matchIndex > lastIndex) {
      const plainText = text.slice(lastIndex, matchIndex)
      elements.push(<span key={`text-${lastIndex}`}>{plainText}</span>)
    }

    const linkText = match[1] ?? matchText
    const href = match[2] ?? match[3]

    elements.push(
      <a
        key={`${href}-${matchIndex}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#458cf7] break-all"
      >
        {linkText}
      </a>
    )

    lastIndex = matchIndex + matchText.length
  }

  if (lastIndex < text.length) {
    elements.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>)
  }

  return elements
}

interface ScrollableContentSectionProps {
  title: string
  content: string
  checkboxLabel: string | React.ReactNode
  isAgreed: boolean
  onAgreedChange: (agreed: boolean) => void
  onNext: () => void
  onBack?: () => void
  showBackButton?: boolean
  nextButtonLabel?: string
  isNextDisabled?: boolean
}

function ScrollableContentSection({
  title,
  content,
  checkboxLabel,
  isAgreed,
  onAgreedChange,
  onNext,
  onBack,
  showBackButton = false,
  nextButtonLabel = "次へ",
  isNextDisabled = false,
}: ScrollableContentSectionProps) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
    }
  }, [hasScrolledToBottom])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    container.addEventListener("scroll", handleScroll)
    return () => container.removeEventListener("scroll", handleScroll)
  }, [handleScroll])

  return (
    <>
      {/* Main Content */}
      <div className="px-[15px] space-y-5">
        {/* Section Title */}
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold text-slate-600 tracking-[-0.3px]">{title}</h2>
        </div>

        {/* Scrollable Content Card */}
        <Card className="h-[365px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)] border-none">
          <div ref={scrollContainerRef} className="h-full overflow-y-auto">
            <div className="p-[15px]">
              <p className="text-xs font-normal text-slate-600 leading-5 whitespace-pre-line">
                {renderContentWithLinks(content)}
              </p>
            </div>
          </div>
        </Card>

        {/* Agreement Checkbox Section */}
        <div className="bg-slate-100 py-[17px] flex items-center justify-center">
          <label
            className={cn(
              "flex items-start gap-[6px] px-4",
              hasScrolledToBottom ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            )}
          >
            <Checkbox
              checked={isAgreed}
              onCheckedChange={(checked) => onAgreedChange(checked === true)}
              disabled={!hasScrolledToBottom}
              className="mt-[2px] size-[18px] rounded border-slate-300 data-[state=checked]:bg-[#354267] data-[state=checked]:border-[#354267]"
            />
            <span className="text-sm font-medium text-slate-600 leading-5 tracking-[-0.168px]">
              {checkboxLabel}
            </span>
          </label>
        </div>
      </div>

      {/* Bottom Buttons */}
      <div className="mt-10 flex flex-col gap-[15px]">
        {showBackButton && onBack && (
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="w-full h-[50px] rounded-[6px] text-sm font-bold border-[#9eafc7] text-[#354267]"
          >
            戻る
          </Button>
        )}
        <Button
          disabled={!isAgreed || isNextDisabled}
          onClick={onNext}
          className={cn(
            "w-full h-[50px] rounded-[6px] text-sm font-bold",
            isAgreed && !isNextDisabled
              ? "bg-[#354267] text-white hover:bg-[#354267]/90"
              : "bg-slate-200 text-slate-400 cursor-not-allowed"
          )}
        >
          {nextButtonLabel}
        </Button>
      </div>
    </>
  )
}

// 確認画面用の表示フィールドコンポーネント
function ConfirmationField({ label, value }: { label: string; value: string | React.ReactNode }) {
  return (
    <div className="flex flex-col items-start w-full">
      <span className="text-sm font-semibold text-[#9eafc7] leading-6">{label}</span>
      <div className="py-[5px]">
        <span className="text-sm font-medium text-[#3f4652] tracking-[-0.168px] leading-5 whitespace-pre-line">
          {value}
        </span>
      </div>
    </div>
  )
}

// 確認画面用の添付ファイル表示コンポーネント
function ConfirmationImageField({ label, attachment }: { label: string; attachment: ImageFile }) {
  const isImage = isImageFile(attachment.file.type, attachment.file.name)

  return (
    <div className="flex flex-col gap-[5px] items-start w-full">
      <span className="text-sm font-semibold text-[#9eafc7] leading-6">{label}</span>
      {isImage ? (
        <div className="w-full h-[165px] rounded-[6px] overflow-hidden">
          <img
            src={attachment.preview}
            alt={label}
            className="w-full h-full object-cover rounded-[6px]"
          />
        </div>
      ) : (
        <div className="flex h-[92px] w-full items-center gap-3 rounded-[6px] bg-slate-100 px-4 text-slate-600">
          <FileText className="size-8 shrink-0 text-slate-400" />
          <span className="min-w-0 truncate text-sm font-semibold">{attachment.file.name}</span>
        </div>
      )}
    </div>
  )
}

// Step 4: 確認画面
function ConfirmationStep({
  data,
  onBack,
  onSubmit,
  isSubmitting = false,
  submitLabel = "上記内容で送信する",
}: {
  data: ConfirmationData
  onBack: () => void
  onSubmit: () => void
  isSubmitting?: boolean
  submitLabel?: string
}) {
  const {
    formValues,
    leaseContractImage,
    importantDocImage,
    otherImages,
    selectedProperty,
    selectedRoom,
  } = data

  return (
    <div className="px-[15px] pb-[40px] space-y-5">
      {/* ①ご契約者情報 */}
      <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
        <div className="flex flex-col gap-4 px-[15px] py-5">
          <FormSectionTitle number={1} title="ご契約者情報" />
          <ConfirmationField
            label="契約区分"
            value={tenantLib.getContractTypeLabel(formValues.contractType)}
          />
          <ConfirmationField label="ご契約者氏名" value={formValues.name} />
          <ConfirmationField label="ご契約者氏名（カナ）" value={formValues.nameKana} />
          <ConfirmationField
            label="生年月日"
            value={formValues.birthday ? dateLib.formatDateObject(formValues.birthday) : "-"}
          />
          <ConfirmationField
            label="電話番号1"
            value={formatPhone(formValues.phone1, formValues.phone2, formValues.phone3)}
          />
          {formValues.phone4 && formValues.phone5 && formValues.phone6 && (
            <ConfirmationField
              label="電話番号2"
              value={formatPhone(formValues.phone4, formValues.phone5, formValues.phone6)}
            />
          )}
          <ConfirmationField label="メールアドレス" value={formValues.email} />
          <ConfirmationField
            label="賃貸借契約開始日"
            value={dateLib.formatDateObject(formValues.leaseStartDate)}
          />
        </div>
      </Card>

      {/* ②物件情報 */}
      <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
        <div className="flex flex-col gap-4 px-[15px] py-5">
          <FormSectionTitle number={2} title="物件情報" />
          <ConfirmationField label="物件名" value={selectedProperty?.name || "-"} />
          <ConfirmationField label="物件名（カナ）" value={selectedProperty?.nameKana || "-"} />
          <ConfirmationField label="部屋番号" value={selectedRoom?.name || "-"} />
          <ConfirmationField label="オーナー名" value={selectedProperty?.owner?.name || "-"} />
          <ConfirmationField
            label="郵便番号"
            value={
              selectedProperty?.upperZip && selectedProperty?.lowerZip
                ? `${selectedProperty.upperZip}-${selectedProperty.lowerZip}`
                : "-"
            }
          />
          <ConfirmationField
            label="住所"
            value={
              selectedProperty
                ? `${selectedProperty.prefecture}${selectedProperty.city}${selectedProperty.town}${selectedProperty.district}` ||
                  "-"
                : "-"
            }
          />
          <ConfirmationField label="用法" value={propertyLib.typeLabel(selectedProperty?.type)} />
          <ConfirmationField
            label="建物構造"
            value={propertyLib.structureLabel(selectedProperty?.structure)}
          />
          <ConfirmationField
            label="用途"
            value={tenantLib.getPurposeLabel(DEFAULT_TENANT_APPLICATION_PURPOSE)}
          />
        </div>
      </Card>

      {/* ③部屋情報 */}
      <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
        <div className="flex flex-col gap-4 px-[15px] py-5">
          <FormSectionTitle number={3} title="部屋情報" />
          <ConfirmationField label="家賃" value={moneyLib.formatYen(formValues.rent)} />
          <ConfirmationField
            label="共益費・管理費"
            value={moneyLib.formatYen(formValues.commonFee)}
          />
          {formValues.hasParking && (
            <>
              <ConfirmationField label="駐車場" value="あり" />
              {formValues.parkingLotNumber && (
                <ConfirmationField label="区画番号" value={formValues.parkingLotNumber} />
              )}
              <ConfirmationField
                label="駐車場代"
                value={moneyLib.formatYen(formValues.parkingFee)}
              />
            </>
          )}
          {formValues.otherFees &&
            formValues.otherFees.length > 0 &&
            formValues.otherFees.map(
              (fee) =>
                fee.name && (
                  <ConfirmationField
                    key={fee.name}
                    label={fee.name}
                    value={moneyLib.formatYenWithTax(fee.baseFee, fee.tax)}
                  />
                )
            )}
        </div>
      </Card>

      {/* ④仲介会社情報 */}
      <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
        <div className="flex flex-col gap-4 px-[15px] py-5">
          <FormSectionTitle number={4} title="仲介会社情報" />
          <ConfirmationField label="仲介会社名" value={formValues.agentCompanyName} />
          <ConfirmationField label="仲介会社 電話番号" value={formValues.agentPhone} />
          {formValues.agentOtherContact && (
            <ConfirmationField label="その他連絡先" value={formValues.agentOtherContact} />
          )}
        </div>
      </Card>

      {/* ⑤ファイル添付 */}
      <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
        <div className="flex flex-col gap-4 px-[15px] py-5">
          <FormSectionTitle number={5} title="ファイル添付" />
          {leaseContractImage && (
            <ConfirmationImageField label="賃貸借契約書" attachment={leaseContractImage} />
          )}
          {importantDocImage && (
            <ConfirmationImageField label="重要事項説明書" attachment={importantDocImage} />
          )}
          {otherImages.map((img, index) => {
            if (!img) return null
            const title = formValues.otherImages?.[index]?.title || `その他ファイル${index + 1}`
            return <ConfirmationImageField key={img.file.name} label={title} attachment={img} />
          })}
        </div>
      </Card>

      {/* ⑥入居者情報 */}
      <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
        <div className="flex flex-col gap-4 px-[15px] py-5">
          <FormSectionTitle number={6} title="入居者情報" />
          {formValues.occupants.length === 0 ? (
            <p className="text-sm text-slate-400">入居者の登録はありません</p>
          ) : (
            formValues.occupants.map((occupant, index) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: 確認画面の表示順固定のためindexを使用
                key={index}
                className="flex flex-col gap-3"
              >
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                  <span className="block h-[1px] w-full bg-slate-200" />
                  <span className="text-sm font-semibold text-slate-300">入居者{index + 1}</span>
                  <span className="block h-[1px] w-full bg-slate-200" />
                </div>
                <ConfirmationField label="氏名" value={occupant.name} />
                <ConfirmationField label="氏名（カナ）" value={occupant.nameKana || "-"} />
                <ConfirmationField label="電話番号" value={occupant.phone || "-"} />
                <ConfirmationField label="生年月日" value={occupant.birthday || "-"} />
                <ConfirmationField label="備考" value={occupant.note || "-"} />
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Buttons */}
      <div className="flex flex-col gap-[15px] pt-5">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
          className="w-full h-[50px] rounded-[6px] text-sm font-bold border-[#9eafc7] text-[#354267]"
        >
          戻る
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="w-full h-[50px] rounded-[6px] text-sm font-bold bg-[#354267] text-white hover:bg-[#354267]/90"
        >
          {isSubmitting ? "送信中..." : submitLabel}
        </Button>
      </div>
    </div>
  )
}

// Step 5: 完了画面
function CompletionStep() {
  return (
    <div className="px-[15px] flex flex-col items-center">
      <div className="w-full max-w-[296px] flex flex-col gap-[15px]">
        <h1 className="text-[20px] font-bold text-[#3f4652] tracking-[-0.24px] leading-6">
          申込完了
        </h1>
        <p className="text-sm font-semibold text-slate-500 tracking-[-0.168px] leading-[18px]">
          申込手続きが完了しました。
          <br />
          ご登録内容は入力いただいたメールアドレス宛にお送りいたします。
        </p>
      </div>
    </div>
  )
}

// Step 3 の状態を管理する型
interface ApplicationFormState {
  otherImageCount: number
  leaseContractImage: ImageFile | null
  importantDocImage: ImageFile | null
  otherImages: (ImageFile | null)[]
  formValues: Partial<ApplicationFormValues>
  selectedProperty: PropertyPublicList200PropertiesItem | null
  selectedRoom: PropertyPublicList200PropertiesItemRoomsItem | null
}

// Step 3: 申込フォーム入力
function ApplicationFormStep({
  onNext,
  onBack,
  formState,
  onFormStateChange,
}: {
  onNext: (data: ConfirmationData) => void
  onBack: () => void
  formState: ApplicationFormState
  onFormStateChange: (state: ApplicationFormState) => void
}) {
  const form = useForm<ApplicationFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: react-hook-form と zod-resolver の型互換性の問題を回避するためにas anyを使用
    resolver: zodResolver(applicationFormSchema) as any,
    mode: "onSubmit", // submitボタンを押した時にバリデーション
    defaultValues: {
      contractType: formState.formValues.contractType ?? undefined,
      name: formState.formValues.name ?? "",
      nameKana: formState.formValues.nameKana ?? "",
      birthday: formState.formValues.birthday ?? undefined,
      phone1: formState.formValues.phone1 ?? "",
      phone2: formState.formValues.phone2 ?? "",
      phone3: formState.formValues.phone3 ?? "",
      phone4: formState.formValues.phone4 ?? "",
      phone5: formState.formValues.phone5 ?? "",
      phone6: formState.formValues.phone6 ?? "",
      email: formState.formValues.email ?? "",
      leaseStartDate: formState.formValues.leaseStartDate ?? undefined,
      roomId: formState.formValues.roomId ?? undefined,
      usage: formState.formValues.usage ?? DEFAULT_TENANT_APPLICATION_PURPOSE,
      rent: formState.formValues.rent ?? "",
      commonFee: formState.formValues.commonFee ?? "",
      hasParking: formState.formValues.hasParking ?? false,
      parkingLotNumber: formState.formValues.parkingLotNumber ?? "",
      parkingFee: formState.formValues.parkingFee ?? "",
      otherFees: formState.formValues.otherFees ?? [{ name: "", baseFee: "", tax: "" }],
      agentCompanyName: formState.formValues.agentCompanyName ?? "",
      agentPhone: formState.formValues.agentPhone ?? "",
      agentOtherContact: formState.formValues.agentOtherContact ?? "",
      otherImages: formState.formValues.otherImages ?? [{ title: "", file: null }],
      occupants: formState.formValues.occupants ?? [],
    },
  })

  // 物件検索フック
  const propertySearch = usePropertySearch()

  const getFormValuesWithFixedPurpose = useCallback(
    () => ({
      ...form.getValues(),
      usage: DEFAULT_TENANT_APPLICATION_PURPOSE,
    }),
    [form]
  )

  // 物件検索用の一時的な状態（適用前）
  const [tempSelectedPropertyId, setTempSelectedPropertyId] = useState<string>(
    formState.selectedProperty?.id ? String(formState.selectedProperty.id) : ""
  )
  const [tempSelectedRoomId, setTempSelectedRoomId] = useState<string>(
    formState.selectedRoom?.id ? String(formState.selectedRoom.id) : ""
  )

  // 物件が変更されたら部屋選択をリセット
  const handlePropertyChange = (propertyId: string) => {
    setTempSelectedPropertyId(propertyId)
    setTempSelectedRoomId("")
    propertySearch.selectProperty(propertyId)
  }

  // 部屋が変更されたとき
  const handleRoomChange = (roomId: string) => {
    setTempSelectedRoomId(roomId)
    propertySearch.selectRoom(roomId)
  }

  // 適用ボタンを押したとき
  const handleApplyProperty = () => {
    if (propertySearch.selectedProperty && propertySearch.selectedRoom) {
      // フォームにroomIdを設定
      form.setValue("roomId", propertySearch.selectedRoom.id)
      // 選択状態を親に保存
      onFormStateChange({
        ...formState,
        selectedProperty: propertySearch.selectedProperty,
        selectedRoom: propertySearch.selectedRoom,
        formValues: getFormValuesWithFixedPurpose(),
      })
    }
  }

  // その他費用のフィールド配列
  const {
    fields: otherFeeFields,
    append: appendOtherFee,
    remove: removeOtherFee,
  } = useFieldArray({
    control: form.control,
    name: "otherFees",
  })

  const {
    fields: occupantFields,
    append: appendOccupant,
    remove: removeOccupant,
  } = useFieldArray({
    control: form.control,
    name: "occupants",
  })

  const watchContractType = form.watch("contractType")
  const isBusiness = false
  const isBirthdayRequired = watchContractType === tenantApplicationType.CONTRACT_TYPE_INDIVIDUAL

  // フォームの値を親に同期
  const syncFormState = useCallback(() => {
    onFormStateChange({
      ...formState,
      formValues: getFormValuesWithFixedPurpose(),
    })
  }, [formState, getFormValuesWithFixedPurpose, onFormStateChange])

  // ファイル・カウント更新時に親に同期
  const updateFormState = useCallback(
    (updates: Partial<ApplicationFormState>) => {
      onFormStateChange({
        ...formState,
        ...updates,
        formValues: getFormValuesWithFixedPurpose(),
      })
    },
    [formState, getFormValuesWithFixedPurpose, onFormStateChange]
  )

  // ファイル選択時にプレビューURLを生成
  const createImageFile = (file: File): ImageFile => ({
    file,
    preview: URL.createObjectURL(file),
  })

  // ファイル削除時にプレビューURLを解放
  const revokeImageUrl = (imageFile: ImageFile | null) => {
    if (imageFile) {
      URL.revokeObjectURL(imageFile.preview)
    }
  }

  // 賃貸借契約書のファイル選択
  const handleLeaseContractImageSelect = (file: File) => {
    revokeImageUrl(formState.leaseContractImage)
    updateFormState({ leaseContractImage: createImageFile(file) })
  }

  // 賃貸借契約書のファイル削除
  const handleLeaseContractImageDelete = () => {
    revokeImageUrl(formState.leaseContractImage)
    updateFormState({ leaseContractImage: null })
  }

  // 重要事項説明書のファイル選択
  const handleImportantDocImageSelect = (file: File) => {
    revokeImageUrl(formState.importantDocImage)
    updateFormState({ importantDocImage: createImageFile(file) })
  }

  // 重要事項説明書のファイル削除
  const handleImportantDocImageDelete = () => {
    revokeImageUrl(formState.importantDocImage)
    updateFormState({ importantDocImage: null })
  }

  // その他ファイルの選択
  const handleOtherImageSelect = (index: number, file: File) => {
    const newImages = [...formState.otherImages]
    revokeImageUrl(newImages[index])
    newImages[index] = createImageFile(file)
    updateFormState({ otherImages: newImages })
  }

  // その他ファイルの削除
  const handleOtherImageDelete = (index: number) => {
    const newImages = [...formState.otherImages]
    revokeImageUrl(newImages[index])
    newImages[index] = null
    updateFormState({ otherImages: newImages })
  }

  // その他ファイルを追加
  const handleAddOtherImage = () => {
    updateFormState({
      otherImageCount: formState.otherImageCount + 1,
      otherImages: [...formState.otherImages, null],
    })
  }

  // 戻るボタンのハンドラ
  const handleBack = () => {
    syncFormState()
    onBack()
  }

  const onSubmit = (data: ApplicationFormValues) => {
    onNext({
      formValues: {
        ...data,
        usage: DEFAULT_TENANT_APPLICATION_PURPOSE,
      },
      leaseContractImage: formState.leaseContractImage,
      importantDocImage: formState.importantDocImage,
      otherImages: formState.otherImages,
      selectedProperty: formState.selectedProperty,
      selectedRoom: formState.selectedRoom,
    })
  }

  // エラー箇所にスクロール
  const scrollToFirstError = useCallback((formErrors: Record<string, unknown>) => {
    // フォームフィールドの優先順位（表示順）
    const fieldOrder = [
      "contractType",
      "name",
      "nameKana",
      "birthday",
      "phone1",
      "phone2",
      "phone3",
      "phone4",
      "phone5",
      "phone6",
      "email",
      "leaseStartDate",
      "rent",
      "commonFee",
      "agentCompanyName",
      "agentPhone",
      "agentOtherContact",
    ]

    // 最初のフォームエラーを探す
    for (const fieldName of fieldOrder) {
      if (formErrors[fieldName]) {
        const element =
          document.querySelector(`[name="${fieldName}"]`) || document.getElementById(fieldName)
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" })
          return
        }
      }
    }
  }, [])

  // フォーム送信時にバリデーションを実行
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // フォームのバリデーションを実行
    const isFormValid = await form.trigger()

    if (isFormValid) {
      form.handleSubmit(onSubmit)()
    } else {
      // エラーがある場合は最初のエラー箇所にスクロール
      setTimeout(() => {
        scrollToFirstError(form.formState.errors)
      }, 100)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={handleFormSubmit} className="space-y-5">
        {/* ①ご契約者情報 */}
        <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
          <div className="flex flex-col gap-4 px-[15px] py-5">
            <FormSectionTitle number={1} title="ご契約者情報" />

            {/* 契約区分 */}
            <FormField
              control={form.control}
              name="contractType"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="契約区分" required />
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10 px-4 py-2 text-sm border-slate-300 rounded-[6px]">
                        <SelectValue placeholder="選択してください" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tenantApplicationType.CONTRACT_TYPES.map((type) => (
                        <SelectItem key={type.key} value={type.key}>
                          {type.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="ご契約者氏名" required />
                  <FormControl>
                    <Input
                      placeholder="田中 太郎"
                      {...field}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nameKana"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="ご契約者氏名（カナ）" required />
                  <FormControl>
                    <Input
                      placeholder="タナカ タロウ"
                      {...field}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="birthday"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="生年月日" required={isBirthdayRequired} />
                  <FormControl>
                    <DatePicker
                      value={field.value ?? undefined}
                      onChange={field.onChange}
                      placeholder="日付を選択"
                      className="rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-[5px]">
              <FormFieldLabel label="電話番号1" required />
              <div className="grid grid-cols-3 gap-2">
                <FormField
                  control={form.control}
                  name="phone1"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="03"
                          {...field}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone2"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="1234"
                          {...field}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone3"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="5678"
                          {...field}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="space-y-[5px]">
              <FormFieldLabel label="電話番号2" />
              <div className="grid grid-cols-3 gap-2">
                <FormField
                  control={form.control}
                  name="phone4"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="03"
                          {...field}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone5"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="1234"
                          {...field}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone6"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          maxLength={4}
                          placeholder="5678"
                          {...field}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="メールアドレス" required />
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="tanaka@example.com"
                      {...field}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leaseStartDate"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="賃貸借契約開始日" required />
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="日付を選択"
                      className="rounded-[6px]"
                      disabled={(date) => date < new Date()}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Card>

        {/* ②物件情報 */}
        <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
          <div className="flex flex-col gap-4 px-[15px] py-5">
            <FormSectionTitle number={2} title="物件情報" />

            {/* 物件検索UI */}
            <div className="flex flex-col gap-4">
              {/* 物件名（サジェスト検索） */}
              <div className="flex flex-col gap-[5px]">
                <FormFieldLabel label="物件名" required />
                <Combobox
                  value={tempSelectedPropertyId}
                  onChange={handlePropertyChange}
                  placeholder="物件名を検索"
                  searchPlaceholder="物件名を入力..."
                  emptyMessage="物件が見つかりません"
                  options={propertySearch.propertyOptions}
                  onSearchChange={propertySearch.handlePropertySearchChange}
                  onLoadMore={propertySearch.handlePropertyLoadMore}
                  onOpen={propertySearch.handlePropertyOpen}
                  isLoading={propertySearch.isLoadingProperties}
                  hasMore={propertySearch.hasMoreProperties}
                  className="h-10"
                />
              </div>

              {/* 部屋番号 */}
              <div className="flex flex-col gap-[5px]">
                <FormFieldLabel label="部屋番号" required />
                <Select
                  value={tempSelectedRoomId}
                  onValueChange={handleRoomChange}
                  disabled={!tempSelectedPropertyId || propertySearch.roomOptions.length === 0}
                >
                  <SelectTrigger className="h-10 px-4 py-2 text-sm border-slate-300 rounded-[6px]">
                    <SelectValue placeholder="部屋番号を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertySearch.roomOptions.map((room) => (
                      <SelectItem key={room.value} value={room.value}>
                        {room.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 適用ボタン */}
              <Button
                type="button"
                onClick={handleApplyProperty}
                disabled={!tempSelectedPropertyId || !tempSelectedRoomId}
                className="h-10 bg-[#354267] text-white hover:bg-[#354267]/90 rounded-[6px] font-bold"
              >
                適用
              </Button>
            </div>

            {/* 物件情報表示 */}
            <div className="flex flex-col gap-4 pt-4 border-t border-slate-200">
              <ReadOnlyField label="物件名" value={formState.selectedProperty?.name || "-"} />
              <ReadOnlyField
                label="物件名（カナ）"
                value={formState.selectedProperty?.nameKana || "-"}
              />
              <ReadOnlyField label="部屋番号" value={formState.selectedRoom?.name || "-"} />
              <ReadOnlyField
                label="郵便番号"
                value={
                  formState.selectedProperty?.upperZip && formState.selectedProperty?.lowerZip
                    ? `${formState.selectedProperty.upperZip}-${formState.selectedProperty.lowerZip}`
                    : "-"
                }
              />
              <ReadOnlyField
                label="住所"
                value={
                  formState.selectedProperty
                    ? `${formState.selectedProperty.prefecture}${formState.selectedProperty.city}${formState.selectedProperty.town}${formState.selectedProperty.district}` ||
                      "-"
                    : "-"
                }
              />
              <ReadOnlyField
                label="用法"
                value={propertyLib.typeLabel(formState.selectedProperty?.type)}
              />
              <ReadOnlyField
                label="建物構造"
                value={propertyLib.structureLabel(formState.selectedProperty?.structure)}
              />
            </div>

            {/* roomIdのバリデーションエラー表示用の非表示フィールド */}
            <FormField
              control={form.control}
              name="roomId"
              render={() => (
                <FormItem className="space-y-0">
                  <FormMessage />
                </FormItem>
              )}
            />

            <ReadOnlyField
              label="用途"
              value={tenantLib.getPurposeLabel(DEFAULT_TENANT_APPLICATION_PURPOSE)}
            />
          </div>
        </Card>

        {/* ③部屋情報 */}
        <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
          <div className="flex flex-col gap-4 px-[15px] py-5">
            <FormSectionTitle number={3} title="部屋情報" />

            {/* 家賃 */}
            <FormField
              control={form.control}
              name="rent"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="家賃" required />
                  <div className="flex items-end gap-[6px] w-full">
                    <div className="flex-1">
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          placeholder="85000"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                    </div>
                    <span className="text-sm font-semibold text-[#3f4652] leading-6">円</span>
                  </div>
                  {isBusiness && (
                    <PriceWithTax
                      baseFee={field.value || "0"}
                      tax={moneyLib.calculateTax(Number(field.value || 0)).toLocaleString()}
                      total={moneyLib.calculateTaxIncluded(Number(field.value || 0))}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 共益費・管理費 */}
            <FormField
              control={form.control}
              name="commonFee"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="共益費・管理費" required />
                  <div className="flex items-end gap-[6px] w-full">
                    <div className="flex-1">
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="numeric"
                          placeholder="5000"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          onKeyDown={handleNumericInput}
                          onPaste={handleNumericPaste}
                          className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                        />
                      </FormControl>
                    </div>
                    <span className="text-sm font-semibold text-[#3f4652] leading-6">円</span>
                  </div>
                  {isBusiness && (
                    <PriceWithTax
                      baseFee={field.value || "0"}
                      tax={moneyLib.calculateTax(Number(field.value || 0)).toLocaleString()}
                      total={moneyLib.calculateTaxIncluded(Number(field.value || 0))}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 駐車場 */}
            <ParkingFields form={form} isBusiness={isBusiness} />

            {/* その他月額費用 */}
            <div className="flex flex-col gap-[5px] w-full">
              <OtherFeesFields
                form={form}
                fields={otherFeeFields}
                append={appendOtherFee}
                remove={removeOtherFee}
                maxCount={3}
              />
            </div>
          </div>
        </Card>

        {/* ④仲介会社情報 */}
        <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
          <div className="flex flex-col gap-4 px-[15px] py-5">
            <FormSectionTitle number={4} title="仲介会社情報" />

            <FormField
              control={form.control}
              name="agentCompanyName"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="仲介会社名" required />
                  <FormControl>
                    <Input
                      placeholder="株式会社○○○○"
                      {...field}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="agentPhone"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="仲介会社 電話番号" required />
                  <FormControl>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      placeholder="09012345678"
                      {...field}
                      onKeyDown={handleNumericInput}
                      onPaste={handleNumericPaste}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="agentOtherContact"
              render={({ field }) => (
                <FormItem className="space-y-[5px]">
                  <FormFieldLabel label="その他連絡先" />
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="tanaka@example.com"
                      {...field}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Card>

        {/* ⑤ファイル添付 */}
        <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
          <div className="flex flex-col gap-4 px-[15px] py-5">
            <FormSectionTitle number={5} title="ファイル添付" />

            {/* 賃貸借契約書 */}
            <div id="lease-contract-image-section" className="flex flex-col gap-[5px] w-full">
              <FormFieldLabel label="賃貸借契約書" />
              {formState.leaseContractImage && (
                <ImagePreview
                  attachment={formState.leaseContractImage}
                  onDelete={handleLeaseContractImageDelete}
                  className="mb-2"
                />
              )}
              <ImageSelectButton
                onSelect={handleLeaseContractImageSelect}
                hasFile={!!formState.leaseContractImage}
              />
            </div>

            {/* 重要事項説明書 */}
            <div id="important-doc-image-section" className="flex flex-col gap-[5px] w-full">
              <FormFieldLabel label="重要事項説明書" />
              {formState.importantDocImage && (
                <ImagePreview
                  attachment={formState.importantDocImage}
                  onDelete={handleImportantDocImageDelete}
                  className="mb-2"
                />
              )}
              <ImageSelectButton
                onSelect={handleImportantDocImageSelect}
                hasFile={!!formState.importantDocImage}
              />
            </div>

            {/* その他ファイル */}
            {Array.from({ length: formState.otherImageCount }).map((_, index) => {
              const otherFile = formState.otherImages[index]
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: indexしかkeyに使えるものがないのでひとまず許可
                <div key={index} className="flex flex-col gap-[10px] w-full">
                  <div className="flex flex-col gap-[5px] w-full">
                    <FormFieldLabel
                      label={`その他ファイル${index + 1 === 1 ? "①" : index + 1 === 2 ? "②" : index + 1 === 3 ? "③" : `${index + 1}`}`}
                    />
                    <Input
                      placeholder="ファイルタイトル"
                      {...form.register(`otherImages.${index}.title`)}
                      className="h-10 px-3 py-2 text-sm border-slate-300 rounded-[6px]"
                    />
                  </div>
                  {otherFile && (
                    <ImagePreview
                      attachment={otherFile}
                      onDelete={() => handleOtherImageDelete(index)}
                      className="mb-2"
                    />
                  )}
                  <ImageSelectButton
                    onSelect={(file) => handleOtherImageSelect(index, file)}
                    hasFile={!!otherFile}
                  />
                </div>
              )
            })}

            {formState.otherImageCount < 3 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleAddOtherImage}
                className="h-10 w-full border-[#9eafc7] text-[#354267] font-bold rounded-[6px]"
              >
                <Plus className="size-[14px] mr-2" />
                ファイルを追加
              </Button>
            )}
          </div>
        </Card>

        {/* ⑥入居者情報 */}
        <Card className="border-slate-300 rounded-[6px] p-0 overflow-hidden">
          <div className="flex flex-col gap-4 px-[15px] py-5">
            <FormSectionTitle number={6} title="入居者情報" />
            <OccupantsFields
              form={form}
              fields={occupantFields}
              append={appendOccupant}
              remove={removeOccupant}
            />
          </div>
        </Card>

        {/* Bottom Buttons */}
        <div className="!mt-10 flex flex-col gap-[15px]">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="w-full h-[50px] rounded-[6px] text-sm font-bold border-[#9eafc7] text-[#354267]"
          >
            戻る
          </Button>
          <Button
            type="submit"
            className="w-full h-[50px] rounded-[6px] text-sm font-bold bg-[#354267] text-white hover:bg-[#354267]/90"
          >
            次へ
          </Button>
        </div>
      </form>
    </Form>
  )
}

// フォーム状態の初期値
const INITIAL_FORM_STATE: ApplicationFormState = {
  otherImageCount: 1,
  leaseContractImage: null,
  importantDocImage: null,
  otherImages: [null],
  formValues: {},
  selectedProperty: null,
  selectedRoom: null,
}

export function NewTenantRegisterPage() {
  const [currentStep, setCurrentStep] = useState(1)
  const [step1Agreed, setStep1Agreed] = useState(false)
  const [step2Agreed, setStep2Agreed] = useState(false)
  const [confirmationData, setConfirmationData] = useState<ConfirmationData | null>(null)
  const [formState, setFormState] = useState<ApplicationFormState>(INITIAL_FORM_STATE)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // tenant-application API
  const createTenantApplication = tenantApplicationApi.useTenantApplicationCreate()

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 5))
  }

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const handleStep3Complete = (data: ConfirmationData) => {
    setConfirmationData(data)
    setCurrentStep(4)
  }

  const handleBackToStep3 = () => {
    setCurrentStep(3)
  }

  const handleSubmit = async () => {
    if (!confirmationData) return

    const { formValues, leaseContractImage, importantDocImage, otherImages } = confirmationData

    setIsSubmitting(true)

    try {
      // ファイルをS3にアップロード
      const uploadFiles: Array<{
        path: string
        fileName: string
        title: string
        tenantFileType:
          | typeof fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT
          | typeof fileType.TENANT_FILE_TYPE_IMPORTANT_MATTERS
          | typeof fileType.TENANT_FILE_TYPE_OTHER_IMAGE
      }> = []

      // 賃貸借契約書のアップロード
      if (leaseContractImage) {
        const result = await uploadFile(leaseContractImage.file)
        uploadFiles.push({
          path: result.key,
          fileName: result.fileName,
          title: fileType.TENANT_FILE_TITLE_RENTAL_CONTRACT,
          tenantFileType: fileType.TENANT_FILE_TYPE_RENTAL_CONTRACT,
        })
      }

      // 重要事項説明書のアップロード
      if (importantDocImage) {
        const result = await uploadFile(importantDocImage.file)
        uploadFiles.push({
          path: result.key,
          fileName: result.fileName,
          title: fileType.TENANT_FILE_TITLE_IMPORTANT_MATTERS,
          tenantFileType: fileType.TENANT_FILE_TYPE_IMPORTANT_MATTERS,
        })
      }

      // その他ファイルのアップロード
      for (let i = 0; i < otherImages.length; i++) {
        const img = otherImages[i]
        if (img) {
          const result = await uploadFile(img.file)
          const title = formValues.otherImages?.[i]?.title || `その他ファイル${i + 1}`
          uploadFiles.push({
            path: result.key,
            fileName: result.fileName,
            title,
            tenantFileType: fileType.TENANT_FILE_TYPE_OTHER_IMAGE,
          })
        }
      }

      // その他費用をAPI形式に変換
      const otherFees =
        formValues.otherFees
          ?.filter((fee) => fee.name && fee.baseFee)
          .map((fee) => ({
            label: fee.name,
            price: Number(fee.baseFee),
            tax: Number(fee.tax || 0),
          })) ?? null

      await createTenantApplication.mutateAsync({
        data: {
          roomId: formValues.roomId,
          name: formValues.name,
          nameKana: formValues.nameKana,
          phone1: formValues.phone1,
          phone2: formValues.phone2,
          phone3: formValues.phone3,
          phone4: formValues.phone4,
          phone5: formValues.phone5,
          phone6: formValues.phone6,
          email: formValues.email,
          birthday: formValues.birthday ? dateLib.formatDateObject(formValues.birthday) : null,
          contractType: formValues.contractType,
          startDate: dateLib.formatDateObject(formValues.leaseStartDate),
          rent: Number(formValues.rent),
          commonAreaFee: Number(formValues.commonFee),
          isParking: formValues.hasParking,
          parkingFee: formValues.hasParking ? Number(formValues.parkingFee || 0) : 0,
          parkingNumber: formValues.parkingLotNumber || "",
          otherFees: otherFees && otherFees.length > 0 ? otherFees : null,
          occupants: toOccupantsPayload(formValues.occupants),
          purpose: DEFAULT_TENANT_APPLICATION_PURPOSE as TenantApplicationCreateBodyPurpose,
          agencyName: formValues.agentCompanyName,
          agencyPhone: formValues.agentPhone,
          otherContact: formValues.agentOtherContact || "",
          uploadFiles: uploadFiles.length > 0 ? uploadFiles : undefined,
        },
      })

      setCurrentStep(5)
    } catch (error) {
      console.error("Failed to submit tenant application:", error)
      // エラーハンドリング（必要に応じてトースト表示など）
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="h-full bg-slate-50">
      {/* Logo */}
      <div className="mb-8 flex justify-center">
        <img src={logo} alt="エージェントコール" className="h-12" />
      </div>

      {/* Step Progress */}
      <div className="pb-8">
        <StepProgress currentStep={currentStep} />
      </div>

      {/* Step 1: サービス利用規約 */}
      {currentStep === 1 && (
        <ScrollableContentSection
          title="入居者総合サービス【○○サポート】利用規約"
          content={SERVICE_TERMS_CONTENT}
          checkboxLabel="利用規約に同意する"
          isAgreed={step1Agreed}
          onAgreedChange={setStep1Agreed}
          onNext={handleNextStep}
        />
      )}

      {/* Step 2: 火災保険意向確認 */}
      {currentStep === 2 && (
        <ScrollableContentSection
          title="火災保険意向確認"
          content={FIRE_INSURANCE_CONTENT}
          checkboxLabel="重要事項説明書を受領するとともに、補償内容・保険料・保険金額・保険責任期間等の契約内容と自身の意向が合致することを確認し、リビングアシスト総合保険（総括契約）に加入します。なお、地震保険は申し込みません。"
          isAgreed={step2Agreed}
          onAgreedChange={setStep2Agreed}
          onNext={handleNextStep}
          onBack={handlePrevStep}
          showBackButton
        />
      )}

      {/* Step 3: 申込フォーム入力 */}
      {currentStep === 3 && (
        <ApplicationFormStep
          onNext={handleStep3Complete}
          onBack={handlePrevStep}
          formState={formState}
          onFormStateChange={setFormState}
        />
      )}

      {/* Step 4: 確認画面 */}
      {currentStep === 4 && confirmationData && (
        <ConfirmationStep
          data={confirmationData}
          onBack={handleBackToStep3}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Step 5: 完了画面 */}
      {currentStep === 5 && <CompletionStep />}
    </div>
  )
}
